/* The words for one line of a card (a library mapping line, a phase setting, a pick list value, a symbol). */

import type { LineRow } from "../../../api/types";
import { lineKindWord, matchWord } from "../shared/words";

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function flag(value: unknown): boolean {
  return value === true;
}

/** A short title for a line, in plain words. */
export function lineTitle(line: LineRow): string {
  const c = line.content as Record<string, unknown>;
  switch (line.kind) {
    case "mapping":
    case "standards": {
      const library = text(c.library) ?? "Library";
      const version = text(c.library_version);
      const head = version ? `${library} ${version}` : library;
      if (c.predicate === "no_match") return `${head}: no match`;
      return `${head}: ${text(c.object_label) ?? text(c.object_id) ?? "(no term)"}`;
    }
    case "phase_setting": {
      const phase = text(c.phase);
      return phase ? `Required from ${phase}` : "Required data";
    }
    case "at_least_one": {
      const phase = text(c.phase);
      return phase ? `At least one, from ${phase}` : "At least one";
    }
    case "pick_value":
      return text(c.title) ?? text(c.code) ?? line.code ?? "Pick list value";
    case "symbol":
      return `Symbol ${text(c.symbol_code) ?? line.code ?? ""}`.trim();
    case "line_style":
      return `Line style ${text(c.style_code) ?? line.code ?? ""}`.trim();
    default:
      return lineKindWord(line.kind);
  }
}

/** The extra sentences that explain a library line: the match word, the reason for "no match", the flags. */
export function mappingNotes(line: LineRow): string[] {
  const c = line.content as Record<string, unknown>;
  const notes: string[] = [];
  const predicate = text(c.predicate);
  if (predicate) notes.push(`Match: ${matchWord(predicate)}`);
  const reason = text(c.no_match_reason);
  if (reason) notes.push(`Reason for no match: ${reason}`);
  const searched = text(c.words_searched);
  if (searched) notes.push(`Words searched: ${searched}`);
  const classes = text(c.library_classes);
  if (classes) notes.push(`Classes the property is assigned to: ${classes}`);
  const comment = text(c.comment);
  if (comment) notes.push(comment);
  if (flag(c.library_not_loaded)) notes.push("This library is not loaded yet.");
  if (flag(c.not_searched_by_meaning_yet)) notes.push("Not searched by meaning yet.");
  if (flag(c.unchecked)) notes.push("The term is not checked against the library file yet.");
  if (flag(c.derived_not_drawn)) notes.push("Made by the app: this element is not drawn on this kind of drawing.");
  return notes;
}

/** True for the two kinds that map an element to a library term. */
export function isLibraryLine(line: LineRow): boolean {
  return line.kind === "mapping" || line.kind === "standards";
}
